// Pure helpers for computing and formatting the demo-day schedule.
// Used by LiveControlPage (organizer) and TeamDashboardPage (per-team slot card).
// No React, no network — easy to unit-test.

export type OrderMode = 'fifo' | 'lifo' | 'alphabetical' | 'theme' | 'shuffle' | 'custom';

export interface SchedulableTeam {
  teamId: string;
  name: string;
  themeName?: string | null;
  // ISO 8601 — when the team first hit "submit". null if they haven't submitted yet.
  submittedAt: string | null;
}

export interface EventSchedule {
  plannedStartAt: string;
  timerDurationSeconds: number;
  teamOrder: string[];
  orderMode: OrderMode;
  updatedBy?: string;
  updatedAt?: string;
}

export interface Slot {
  position: number; // 1-indexed
  teamId: string;
  startsAt: Date;
  endsAt: Date;
  durationSeconds: number;
}

/**
 * Apply an order preset to a set of teams and return the resulting teamId array.
 * Callers can then hand-tweak (which should flip their UI state to `custom`).
 *
 * FIFO  — by submission time ascending; teams without a submission go to the end
 *         sorted alphabetically so the tail is still deterministic.
 * LIFO  — by submission time descending; unsubmitted teams go last.
 * Alpha — by team name, case-insensitive, natural-number-aware via localeCompare.
 * Theme — group by themeName then alpha within each group. No theme = last group.
 * Shuffle — Fisher–Yates with the supplied rng (default Math.random).
 * Custom — identity; pass-through so callers can treat it as a no-op preset.
 */
export function applyOrderMode(
  teams: SchedulableTeam[],
  mode: OrderMode,
  rng: () => number = Math.random,
): string[] {
  const copy = [...teams];
  switch (mode) {
    case 'fifo':
      return copy
        .sort((a, b) => compareNullable(a.submittedAt, b.submittedAt, 1) || a.name.localeCompare(b.name))
        .map((t) => t.teamId);
    case 'lifo':
      return copy
        .sort((a, b) => compareNullable(a.submittedAt, b.submittedAt, -1) || a.name.localeCompare(b.name))
        .map((t) => t.teamId);
    case 'alphabetical':
      return copy
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
        .map((t) => t.teamId);
    case 'theme': {
      return copy
        .sort((a, b) => {
          const themeCmp = (a.themeName ?? '￿').localeCompare(b.themeName ?? '￿');
          return themeCmp !== 0 ? themeCmp : a.name.localeCompare(b.name);
        })
        .map((t) => t.teamId);
    }
    case 'shuffle': {
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy.map((t) => t.teamId);
    }
    case 'custom':
      return copy.map((t) => t.teamId);
  }
}

// ISO-string comparator that pushes nulls to the end regardless of direction.
function compareNullable(a: string | null, b: string | null, direction: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1; // null always last
  if (b === null) return -1;
  return direction === 1 ? a.localeCompare(b) : b.localeCompare(a);
}

/**
 * Compute each team's predicted slot assuming they start at plannedStartAt
 * back-to-back with no breaks (durationSeconds each). The schedule is advisory
 * until the session actually starts.
 */
export function computeSlots(
  teamOrder: string[],
  plannedStartAt: string | Date,
  durationSeconds: number,
): Slot[] {
  const start = typeof plannedStartAt === 'string' ? new Date(plannedStartAt) : plannedStartAt;
  const startMs = start.getTime();
  const durMs = durationSeconds * 1000;
  return teamOrder.map((teamId, i) => ({
    position: i + 1,
    teamId,
    startsAt: new Date(startMs + i * durMs),
    endsAt: new Date(startMs + (i + 1) * durMs),
    durationSeconds,
  }));
}

/**
 * Given a saved schedule and the current list of teams, return the slot for one
 * team — or null if they're not in the schedule (e.g. added after it was saved).
 */
export function findSlotForTeam(schedule: EventSchedule, teamId: string): Slot | null {
  const slots = computeSlots(schedule.teamOrder, schedule.plannedStartAt, schedule.timerDurationSeconds);
  return slots.find((s) => s.teamId === teamId) ?? null;
}

/**
 * Human-readable relative label for a slot. "in 14 min" / "presenting now" /
 * "already done". Assumes `now` is a sane Date; callers can pass new Date() or
 * a testable fixed value.
 */
export function slotRelativeLabel(slot: Slot, now: Date = new Date()): string {
  const nowMs = now.getTime();
  if (nowMs >= slot.startsAt.getTime() && nowMs < slot.endsAt.getTime()) {
    return 'presenting now';
  }
  if (nowMs >= slot.endsAt.getTime()) {
    return 'already done';
  }
  const diffMs = slot.startsAt.getTime() - nowMs;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'starting in <1 min';
  if (mins < 60) return `starts in ${mins} min`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins === 0 ? `starts in ${hours}h` : `starts in ${hours}h ${remMins}m`;
}

/**
 * Short time format "HH:MM" in the user's local timezone. For calling-out slot
 * starts in lists.
 */
export function formatSlotTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Long format with date + time, for the Team Dashboard "Your demo slot" card.
 */
export function formatSlotDateTime(date: Date): string {
  return date.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Build a plain-text schedule suitable for pasting into Slack/email. Keeps it
 * minimal so organizers can edit before sending.
 */
export function buildShareableText(
  schedule: EventSchedule,
  teamsById: Record<string, { name: string; themeName?: string | null }>,
  eventName: string,
): string {
  const slots = computeSlots(schedule.teamOrder, schedule.plannedStartAt, schedule.timerDurationSeconds);
  const minsPerTeam = Math.round(schedule.timerDurationSeconds / 60);
  const startDate = new Date(schedule.plannedStartAt);
  const lines: string[] = [
    `Demo day schedule — ${eventName}`,
    `Starts ${formatSlotDateTime(startDate)} · ${minsPerTeam} min per team · ${slots.length} teams`,
    `Times shown in local time (${Intl.DateTimeFormat().resolvedOptions().timeZone}).`,
    '',
  ];
  for (const slot of slots) {
    const team = teamsById[slot.teamId];
    const name = team?.name ?? slot.teamId;
    const theme = team?.themeName ? ` — ${team.themeName}` : '';
    lines.push(`${slot.position}. ${formatSlotTime(slot.startsAt)}–${formatSlotTime(slot.endsAt)}  ${name}${theme}`);
  }
  return lines.join('\n');
}

export const ORDER_MODE_LABELS: Record<OrderMode, { label: string; hint: string }> = {
  fifo: { label: 'Submission time — earliest first', hint: 'FIFO: teams that submitted earliest go first.' },
  lifo: { label: 'Submission time — latest first', hint: 'LIFO: teams that submitted latest go first.' },
  alphabetical: { label: 'Alphabetical', hint: 'By team name, A → Z.' },
  theme: { label: 'Group by theme', hint: 'Teams working the same theme present back-to-back.' },
  shuffle: { label: 'Random shuffle', hint: 'Fresh random order — click again for a new draw.' },
  custom: { label: 'Custom', hint: 'Manual order. Any drag or up/down click puts you here.' },
};
